import {Injectable} from '@angular/core';
import {LocalStorageKeyType, LocalStorageKeysEnum} from '../models/local-storage-keys';

/**
 * Service for interacting with the browser's local storage.
 *
 * This service provides a set of utility methods to get, set, remove, and clear local storage
 * items in a structured and type-safe way. It is designed to work with keys defined in the `LocalStorageKeysEnum`.
 *
 * @service
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {

  /**
   * Clears all items from the browser's local storage.
   *
   * This method will remove all key-value pairs stored in local storage, effectively
   * resetting the local storage for the application.
   */
  clearLocalStorageKeys(): void {
    localStorage.clear();
  }

  /**
   * Retrieves all items from local storage and returns them as an object.
   *
   * This method iterates over all keys defined in `LocalStorageKeysEnum` and fetches the
   * corresponding values from local storage.
   *
   * @returns {Object} An object containing the key-value pairs from local storage.
   *                   Each key corresponds to an item in `LocalStorageKeysEnum` and
   *                   the value is the stringified value from local storage or null.
   */
  getLocalStorage(): Record<string, string | null> {
    const localObject: Record<string, string | null> = {};
    Object.values(LocalStorageKeysEnum).forEach((key: LocalStorageKeyType) => {
      localObject[key] = this.getLocalStorageItem(key);
    });
    return localObject;
  }

  /**
   * ### getLocalStorageItem
   * Retrieves and parses a JSON-encoded item from local storage.
   *
   * @template DataType - The expected return type of the parsed data.
   * @param {string} key - The key under which the item is stored in `localStorage`.
   * @returns {DataType | null} The parsed object if found and valid, or `null` if not present.
   *
   * @example
   * const user = this.localStorageService.getLocalStorageItem<IUser>('current-user');
   * if (user) {
   *   console.info(user.name);
   * }
   */
  getLocalStorageItem<DataType>(key: string): DataType | null {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        return JSON.parse(data) as DataType;
      } catch (e) {
        console.error('Error parsing JSON from localStorage', e);
        localStorage.removeItem(key);
      }
    }
    return null;
  }

  /**
   * Removes a specific item from local storage by key.
   *
   * This method deletes the item associated with the specified key in local storage.
   *
   * @param {LocalStorageKeyType} key The key for the item to be removed from local storage.
   */
  removeLocalStorageItem(key: LocalStorageKeyType): void {
    localStorage.removeItem(key);
  }

  /**
   * Sets a value in local storage for a given key.
   *
   * This method stores a value in local storage under the specified key. If the value is
   * not already a string, it will be stringified before storing.
   *
   * @template DataType
   * @param {LocalStorageKeyType} key The key under which the value should be stored.
   * @param {DataType | string} value The value to store. Can be any type, which will be stringified if it's not already a string.
   */
  setLocalStorageItem<DataType>(key: LocalStorageKeyType, value: DataType | string | null): void {
    // Prevent storing null or undefined values
    if (value === null || value === undefined) {
      return;
    }
    try {
      localStorage.setItem(
        key,
        typeof value === 'string' ? value : JSON.stringify(value),
      );
    } catch (e) {
      console.error('Error setting localStorage item', e);
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.error('Local storage quota has been exceeded.');
      }
    }
  }

  /**
   * ### getEditedOrderStockItemsFromLocalStorage$
   * Triggered by `getEditedOrderStockItemsFromLocalStorage` to retrieve locally saved edited stock items.
   *
   * @effect
   * @trigger AutoOrderActions.getEditedOrderStockItemsFromLocalStorage
   * - Checks if user is logged in.
   * - Constructs a local storage key using `generateLocalStorageKey`.
   * - Reads edited stock items from local storage.
   * - Updates `progress.localStorageEditedItems` and emits success.
   *
   * @returns AutoOrderActions.getEditedOrderStockItemsFromLocalStorageSuccess or
   *          NotificationActions.setGlobalErrorOccurrence
   */
  generateLocalStorageKey(keyFromEnum: LocalStorageKeysEnum, property: string): string {
    return `${keyFromEnum}-${property}`;
  }

}
